import type { AppRouter } from "@postdaily/api/routers/index";
import { env } from "@postdaily/env/web";

import "./index.css";
import { Spinner } from "@postdaily/ui/components/spinner";
import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { setupRouterSsrQueryIntegration } from "@tanstack/react-router-ssr-query";
import { createTRPCClient, httpBatchLink } from "@trpc/client";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import superjson from "superjson";
import { getPlanLimitCause } from "./lib/mutation-error";
import { routeTree } from "./routeTree.gen";
import { useUpgradeDialogStore } from "./stores/upgrade-dialog-store";
import { TRPCProvider } from "./utils/trpc";

export const queryClient = new QueryClient({
  queryCache: new QueryCache(),
  // Any mutation that trips a plan limit (connect a channel, schedule, Post Now,
  // AI, invite, media…) opens the upgrade dialog with the limit's context —
  // centrally, so no per-call-site wiring is needed.
  mutationCache: new MutationCache({
    onError: (error) => {
      if (typeof window === "undefined") {
        return;
      }

      const cause = getPlanLimitCause(error);
      if (!cause) {
        return;
      }

      useUpgradeDialogStore.getState().openUpgradeDialog({
        reason: error instanceof Error ? error.message : undefined,
        feature: cause.feature,
        highlightPlan: cause.upgradePlanCode,
      });
    },
  }),
  defaultOptions: { queries: { staleTime: 60 * 1000 } },
});

const trpcClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      // The API refuses batches over 50 (`maxBatchSize`); stay well under.
      maxItems: 20,
      url: `${env.VITE_SERVER_URL}/trpc`,
      transformer: superjson,
      fetch(url, options) {
        return fetch(url, {
          ...options,
          credentials: "include",
        });
      },
    }),
  ],
});

const trpc = createTRPCOptionsProxy({
  client: trpcClient,
  queryClient: queryClient,
});

export const getRouter = () => {
  const router = createTanStackRouter({
    routeTree,
    scrollRestoration: true,
    // No `defaultNotFoundComponent` here on purpose: `__root` declares
    // `notFoundComponent`, which covers the whole tree, and a route file's is
    // code-split while this one is not — setting it here dragged the marketing
    // header (and with it the auth client) into the entry chunk of every page.
    defaultPreloadStaleTime: 0,
    context: { trpc, queryClient },
    defaultPendingComponent: () => (
      <div className="flex h-screen w-screen flex-1 items-center justify-center">
        <Spinner className="size-10" />
      </div>
    ),
    Wrap: ({ children }) => (
      <TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
        {children}
      </TRPCProvider>
    ),
  });

  setupRouterSsrQueryIntegration({
    router,
    queryClient,
  });

  return router;
};

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
